<template>
  <div class="sign-up">
    <Loader v-if="loading" />

    <!-- Background -->
    <div class="sign-up__background" v-html="require('@/assets/img/reg-background.svg')"></div>

    <div class="sign-up__wrapper" v-if="!isRegistered && mountedComplete">
      <HeaderLogo :serviceLink="serviceLink" :service="service" class="pt-9" />

      <div class="sign-up__form form">
        <!-- Заголовок -->
        <bim-text type="accent" bold class="form__title">
          {{ $bimTranslate("signUp.registration", activeLang) }}
        </bim-text>

        <!-- Текущий шаг -->
        <bim-text type="desc" bold class="form__step mt-6 mb-10">
          {{ $bimTranslate("signUp.step", activeLang) }}
          {{ step.current }}
          {{ $bimTranslate("signUp.of", activeLang) }}
          {{ step.count }}
        </bim-text>

        <!-- Первый шаг -->
        <StepOne
          v-show="step.current === 1"
          v-model="form"
          @next="stepOneSuccess"
          :activeLang="activeLang"
          :roleType="roleType"
        />

        <!-- Второй шаг -->
        <StepTwo
          v-show="step.current === 2"
          v-model="form"
          @skip="validateCapcha"
          @finish="validateCapcha"
          :activeLang="activeLang"
          :companyRequired="companyRequired"
        />
        <!-- капча -->
        <vue-recaptcha
          v-show="step.current === 2"
          ref="recaptcha"
          size="invisible"
          :sitekey="sitekey"
          :loadRecaptchaScript="true"
          @verify="stepTwoSuccess"
          @expired="onCaptchaExpired"
        />

        <!-- Переход на авторизацию -->
        <a class="form__link" href="https://auth.bimlib.pro/login" @click.prevent="openLoginPage">
          <bim-text type="medium" class="form__link-text">
            {{ $bimTranslate("signUp.hasAccount", activeLang) }}
          </bim-text>
        </a>
      </div>

      <ChangeLanguage :lang="activeLang" @changeLang="changeLang" class="sign-up__lang" />

      <TermsBlock :activeLang="activeLang" class="sign-up__footer" />
    </div>

    <bimPopup
      :value="modal.success"
      @input="openLoginPage"
      :title="$bimTranslate('signUp.successPopup.title', activeLang)"
      class="success-popup"
    >
      <bim-text type="main">
        {{ $bimTranslate("signUp.successPopup.description", activeLang) }}
      </bim-text>
      <bim-button
        class="d-block mt-6 success-popup__button"
        @click="openLoginPage"
        :label="$bimTranslate('signUp.successPopup.button', activeLang)"
      />
    </bimPopup>
  </div>
</template>

<script>
import bimText from '@/components/text/bimText/bimText'
import bimPopup from '@/components/ui/bimPopup/bimPopup'
import bimButton from '@/components/form/bimButton/bimButton'

import HeaderLogo from '@/components/core/wizardRegForm/HeaderLogo'
import TermsBlock from '@/components/core/wizardRegForm/TermsBlock'
import ChangeLanguage from '@/components/core/wizardRegForm/ChangeLanguage'

import StepOne from '@/components/core/wizardRegForm/StepOne'
import StepTwo from '@/components/core/wizardRegForm/StepTwo'

import Loader from '@/components/core/loader'

import VueRecaptcha from 'vue-recaptcha'

import { urlParams } from '@/utils/url'

export default {
  name: 'WizardRegForm',
  components: {
    bimText,
    bimPopup,
    bimButton,

    HeaderLogo,
    TermsBlock,
    ChangeLanguage,

    StepOne,
    StepTwo,

    VueRecaptcha,

    Loader,
  },
  props: {
    /**
     * текущий язык
     */
    activeLang: {
      type: String,
      default: 'ru',
      validator (value) {
        return ['ru', 'en'].indexOf(value) !== -1
      },
    },
    /**
     * объект контекста пользователя
     */
    context: {
      type: Object,
    },
    /**
     * Среда исполнения (дев стейдж прод)
     */
    appEnv: {
      type: String,
      default: 'prod',
    },
    /**
     * тип сервиса
     */
    service: {
      type: String,
      default: 'bimlib',
      validator (service) {
        return ['bimlib', 'costx'].includes(service)
      },
    },
    /**
     * ссылка сервиса
     */
    serviceLink: {
      type: String,
      default: () => {
        try {
          return window.location.origin
        } catch (e) {
          return '/'
        }
      },
    },
    /**
     * Функция для перехода не другую страницу при успешной регистрации
     * первый аргумент - контекст пользователя
     */
    redirectPageCallback: {
      type: Function,
      default: null,
    },
    /**
     * Логирующая функция
     */
    logActionEvent: {
      type: Function,
      default: () => {},
    },
    /**
     * обязательно ли поле компания для заполнения
     */
    companyRequired: Boolean,
    /**
     * забить роль пользователя
     */
    roleType: [String, Boolean],
    /**
     * captcha siteKey
     */
    sitekey: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      captcha: false,
      mountedComplete: false,
      modal: {
        success: false,
      },
      loading: false,
      step: {
        count: 2,
        current: 1,
      },
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '+7',
        context: {
          agree: false,
          promoAgree: false,
          role: '',
          companyName: '',
          specialisation: '',
        },
      },
    }
  },
  watch: {
    context (val) {
      if (val && val.id) {
        this.$set(this.form.context, 'agree', true)
        if (this.roleType) {
          this.$set(this.form.context, 'role', this.roleType)
        }
      }
    },
  },
  async created () {
    this.loading = true

    const user = this.context || {}

    const service = this.serviceLink || urlParams.get('service')
    if (service) {
      localStorage.setItem('tmpServiceHost', service)
    }

    if (this.isRegistered) {
      return this.redirect(service)
    }

    const token = urlParams.get('token')
    if (token) {
      localStorage.setItem('tmpToken', token)
      await this.confirmEmail(token)
      return
    }

    if (user && user.id) {
      if (user.context) user.context.agree = true
      else user.context = { agree: true }

      this.form = Object.assign(this.form, user)
    }

    /* вход через соц. сети */
    const { id, source } = user
    if (source && id) {
      this.mountedComplete = true
      this.loading = false
      return
    }

    /* есть id - обновляем контекст и редиректим */
    if (id) {
      try {
        await this.$user.setParams({
          registered: true,
        })
        return this.redirect(service)
      } catch (e) {}
    }

    this.mountedComplete = true
    this.loading = false
  },
  mounted () {
    this.logActionEvent('SignUpOpenPage')
  },
  computed: {
    isRegistered () {
      const context = (this.context || {}).context
      return (context || {}).registered
    },
  },
  methods: {
    resetData () {
      this.captcha = false
      this.mountedComplete = true
      this.modal.success = false
      this.loading = false
      this.step.count = 2
      this.step.current = 1
      this.form.firstName = ''
      this.form.lastName = ''
      this.form.email = ''
      this.form.phone = '+7'
      this.form.context.agree = false
      this.form.context.role = ''
      this.form.context.companyName = ''
      this.form.context.specialisation = ''
    },
    // вызов капчи
    validateCapcha () {
      this.$refs.recaptcha.execute()
    },
    onCaptchaExpired () {
      this.$refs.recaptcha.reset()
    },
    redirect (service) {
      if (this.redirectPageCallback && typeof this.redirectPageCallback === 'function') {
        return this.redirectPageCallback(this.form)
      }

      window.location.href = service || this.serviceLink || '/'
    },

    /* подтверждение токена */
    async confirmEmail (token) {
      try {
        // создадим сессию для авторизации
        await this.$user.createSession(this.appEnv, token)
      } catch (e) {
        console.error('confirmEmail', e)
      } finally {
        this.openLoginPage('web', this.appEnv, false)
      }
    },

    changeLang (lang) {
      /**
       * изменение языка
       */
      this.$emit('changeLang', lang)
    },

    /* завершение первого шага */
    stepOneSuccess () {
      this.step.current++
      this.logActionEvent('SignUpSuccessStepOne', this.form)
    },

    /* завершение второго шага */
    async stepTwoSuccess (recaptchaToken) {
      this.loading = true

      // сохранять токен при завершении второго этапа
      this.captcha = recaptchaToken
      try {
        /* зашли через соц. сети */
        if (this.form.id) {
          await this.$user.updateContext(this.form)
          await this.$user.setParams({
            registered: true,
          })
          this.redirect()
        } else {
          await this.registerUser()
          this.modal.success = true
        }
      } catch (e) {
        console.log(e)
      } finally {
        this.loading = false
      }

      const phone = (this.form.phone || '').match(/\d/g) || []
      if (phone.length <= 1) {
        this.form.phone = ''
      }

      if (
        (this.form.phone.length || '') &&
        (this.form.context.specialisation || this.form.context.companyName)
      ) {
        this.logActionEvent('SignUpFinishSecondStepSuccess', this.form)
      } else {
        this.logActionEvent('SignUpFinishSecondStepSkip', this.form)
      }
    },

    /* кнопка пропустить второй шаг */
    skipSecondStep () {
      this.logActionEvent('SignUpSkipSecondStep', this.form)

      this.form.context.companyName = ''
      this.form.phone = ''
    },

    openLoginPage () {
      return this.$user.openLoginPage('web', this.appEnv, false)
    },

    /* регистрация пользователя */
    registerUser () {
      /** генерация случайной аватарки */
      const iconNumber = Math.ceil(Math.random() * 15)

      /** если телефон не заполнен, то не отправляем, иначе ошибка */
      if (!this.form.phone) {
        delete this.form.phone
      }

      // регистрация вместе с токеном капчи
      return this.$user
        .signUp(
          {
            user: {
              ...this.form,
              avatar: this.form.avatar || `Avatar${iconNumber}`,
            },
            service: this.serviceLink || urlParams.get('service'),
          },
          this.captcha
        )
        .then((res) => {
          if (res.code === 'constraint.user.email.unique') {
            this.$toast.error(this.$bimTranslate('signUp.emailTaken', this.activeLang))
            this.resetData()
            this.onCaptchaExpired()
            throw new Error(res.code)
          } else if (res.code === 'Token assessment has been failed for the reason: invalid-keys') {
            this.$toast.error('Произошла ошибка при регистрации. Пожалуйста, попробуйте еще раз.')
            this.resetData()
            this.onCaptchaExpired()
            throw new Error(res.code)
          } else if (
            res.code === 'Token assessment has been failed for the reason: invalid-input-response'
          ) {
            this.$toast.error('Произошла ошибка при регистрации. Пожалуйста, попробуйте еще раз.')
            this.resetData()
            this.onCaptchaExpired()
            throw new Error(res.code)
          }

          try {
            /* регистрация производителя */
            if (this.form.role === 'manufacturer') {
              window.yaCounter40131035.reachGoal('registration-manufacturer')
              window.dataLayer.push({
                event: 'EVENT2GA',
                eventCategory: 'PROFILE',
                eventAction: 'SIGNUP_MANUFACTURER',
              })
            } else {
              /* регистрация пользователя */
              window.yaCounter40131035.reachGoal('registration')
              window.dataLayer.push({
                event: 'EVENT2GA',
                eventCategory: 'PROFILE',
                eventAction: 'SIGNUP',
              })
            }
          } catch (e) {}
          // записываем в эластик, если пользователь согласен на получение рекламы
          if (this.form.context.promoAgree) {
            this.logActionEvent('UserPromoAgree', {
              form: this.form
            })
          }

          return res
        })
        .catch((e) => {
          throw e
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.sign-up {
  position: relative;
  z-index: 1;
  background-color: #f6f6f6;
  min-height: 100vh;

  .sign-up__form,
  .sign-up__lang {
    max-width: 445px;
  }

  .sign-up__form {
    width: 100%;
    min-height: 543px;
    background: white;
    border-radius: 5px;
    border: 1px solid #ededed;
    padding: 32px;
    margin: 81px auto 0;

    @media screen and (max-width: 576px) {
      margin-top: 32px;
      padding: 24px 12px;
    }
  }

  .sign-up__lang {
    position: absolute;
    top: 40px;
    right: 40px;
    width: 80px !important;

    ::v-deep {
      input {
        background: transparent;
      }

      .select__label {
        right: 0;
      }
    }

    @media screen and (max-width: 576px) {
      position: relative;
      top: initial;
      right: initial;
      width: 100% !important;
      margin: 24px auto;
    }
  }

  .sign-up__background {
    position: absolute;
    z-index: -1;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 78%;
  }

  .sign-up__wrapper {
    padding: 40px 24px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;

    @media screen and (max-width: 576px) {
      padding: 24px 0;
    }
  }

  .sign-up__footer {
    margin-top: auto;
    padding: 20px 0 0 !important;
  }
}

.form {
  display: flex;
  flex-flow: column nowrap;

  .form__title,
  .form__step {
    text-align: center;
  }

  .form__step {
    text-transform: uppercase;
    opacity: 0.5;
  }

  .form__link {
    margin-top: auto;
    text-align: center;
    color: inherit;
    text-decoration: none;

    .form__link-text {
      display: inline;
      border-bottom: 1px solid #00afa1;
    }
  }
}

.success-popup {
  .success-popup__button {
    margin: auto;
    display: block;
  }
}
</style>
